import React, { useEffect, useState } from 'react';
import axios from 'axios';
import AppHeader from '../components/AppHeader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  ImagePlus, Trash2, Loader2, ImageIcon, ChevronUp, ChevronDown, Eye
} from 'lucide-react';
import { brandName, brandEmail, brandPhone, brandLogo, brandWebsite } from '../lib/brand';

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const PastWork = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editingCaption, setEditingCaption] = useState(null);
  const [captionDraft, setCaptionDraft] = useState('');

  const load = async () => {
    try {
      const res = await axios.get('/past-work');
      setItems(res.data || []);
    } catch (e) {
      toast.error('Failed to load past work');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        if (!file.type.startsWith('image/')) {
          toast.error(`${file.name} is not an image`);
          continue;
        }
        if (file.size > 5 * 1024 * 1024) {
          toast.error(`${file.name} is too large (max 5MB)`);
          continue;
        }
        const dataUrl = await fileToBase64(file);
        const res = await axios.post('/past-work', {
          caption: '',
          image_base64: dataUrl,
        });
        setItems(prev => [...prev, res.data]);
      }
      toast.success('Photos uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Remove this photo from the marketing site gallery?')) return;
    try {
      await axios.delete(`/past-work/${id}`);
      setItems(prev => prev.filter(i => i.id !== id));
      toast.success('Removed');
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const saveCaption = async (id) => {
    try {
      const res = await axios.patch(`/past-work/${id}`, { caption: captionDraft });
      setItems(prev => prev.map(i => (i.id === id ? res.data : i)));
      setEditingCaption(null);
      toast.success('Caption saved');
    } catch (e) {
      toast.error('Save failed');
    }
  };

  const move = async (id, direction) => {
    const idx = items.findIndex(i => i.id === id);
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= items.length) return;
    const reordered = [...items];
    [reordered[idx], reordered[newIdx]] = [reordered[newIdx], reordered[idx]];
    setItems(reordered);
    try {
      await Promise.all(
        reordered.map((item, i) => axios.patch(`/past-work/${item.id}`, { sort_order: i + 1 }))
      );
    } catch (e) {
      toast.error('Reorder failed');
      load();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="past-work-page">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <ImageIcon className="w-7 h-7 text-purple-600" />Past Work Gallery
            </h1>
            <p className="text-gray-600 text-sm mt-1">
              These photos appear in the gallery section of your marketing site ({brandWebsite() || 'storefront'})
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" asChild data-testid="preview-site-btn">
              <a href="/" target="_blank" rel="noopener noreferrer">
                <Eye className="w-4 h-4 mr-2" />Preview Site
              </a>
            </Button>
            <label className="cursor-pointer">
              <input type="file" accept="image/*" multiple
                onChange={handleUpload} className="hidden"
                data-testid="upload-input" />
              <Button asChild disabled={uploading}>
                <span className="bg-purple-600 hover:bg-purple-700 text-white inline-flex items-center px-4 py-2 rounded cursor-pointer">
                  {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ImagePlus className="w-4 h-4 mr-2" />}
                  Upload Photos
                </span>
              </Button>
            </label>
          </div>
        </div>

        {loading ? (
          <div className="bg-white rounded-xl p-20 text-center shadow-sm">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600 mx-auto" />
          </div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-xl p-16 text-center shadow-sm border-2 border-dashed border-gray-300">
            <ImageIcon className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No photos yet</h3>
            <p className="text-gray-500 mb-6 max-w-md mx-auto">
              Upload photos of your best printed shirts, embroidered polos, team uniforms, and event apparel.
              They'll appear in the gallery section of your marketing site.
            </p>
            <label className="cursor-pointer inline-block">
              <input type="file" accept="image/*" multiple onChange={handleUpload} className="hidden"
                data-testid="upload-input-empty" />
              <span className="bg-purple-600 hover:bg-purple-700 text-white inline-flex items-center px-6 py-3 rounded-lg cursor-pointer">
                <ImagePlus className="w-5 h-5 mr-2" />Upload Your First Photos
              </span>
            </label>
            <p className="text-xs text-gray-400 mt-4">JPG / PNG · Max 5MB per file · Upload multiple at once</p>
          </div>
        ) : (
          <>
            <div className="text-sm text-gray-600 mb-4">
              {items.length} photo{items.length !== 1 ? 's' : ''} · Drag-equivalent reorder with ↑↓ buttons
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {items.map((item, idx) => (
                <div key={item.id} className="bg-white rounded-xl shadow-sm overflow-hidden group"
                  data-testid={`past-work-${item.id}`}>
                  <div className="relative aspect-square bg-gray-100">
                    <img src={item.image_base64} alt={item.caption || ''}
                      className="w-full h-full object-cover" />
                    <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded font-mono">
                      #{idx + 1}
                    </div>
                    <div className="absolute top-2 right-2 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition">
                      <button onClick={() => move(item.id, -1)} disabled={idx === 0}
                        className="bg-white/90 hover:bg-white text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed rounded p-1.5 shadow"
                        title="Move up">
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button onClick={() => move(item.id, 1)} disabled={idx === items.length - 1}
                        className="bg-white/90 hover:bg-white text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed rounded p-1.5 shadow"
                        title="Move down">
                        <ChevronDown className="w-4 h-4" />
                      </button>
                      <button onClick={() => deleteItem(item.id)}
                        className="bg-red-500 hover:bg-red-600 text-white rounded p-1.5 shadow"
                        title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="p-3">
                    {editingCaption === item.id ? (
                      <div className="flex gap-1">
                        <Input value={captionDraft} onChange={e => setCaptionDraft(e.target.value)}
                          placeholder="Caption" className="h-8 text-sm"
                          onKeyDown={(e) => e.key === 'Enter' && saveCaption(item.id)} />
                        <Button size="sm" onClick={() => saveCaption(item.id)} className="h-8">Save</Button>
                      </div>
                    ) : (
                      <button onClick={() => { setEditingCaption(item.id); setCaptionDraft(item.caption || ''); }}
                        className="text-sm text-gray-600 w-full text-left truncate hover:text-purple-600">
                        {item.caption || <span className="text-gray-400 italic">Add caption…</span>}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default PastWork;
